import React, { useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { FormGroup, FormControlLabel, Checkbox, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { ALL_DAYS, CHARGING_DAYS, Day, dayToIndex } from '@e3dc-react/shell/Libs/date/types'

const StyledFormControlLabel = styled(FormControlLabel)(({ theme, checked }) => ({
  transition: 'background-color 0.3s ease',
  backgroundColor: checked ? theme.palette.primary.light : 'transparent',
  padding: theme.spacing(0, 2),
  margin: 0,
}))

const useStyles = makeStyles()(theme => ({
  dialog: {
    '& .MuiDialogContent-root, .MuiDialog-paper': {
      padding: 0,
    },
  },
  checkboxText: {
    paddingTop: '3px',
  },
}))

export interface WallboxWeekDaysDialogProps {
  onClose?: () => void
  place?: number
  currentWeekDayIndex: number
  updateSelectedDays: (selectedDays: Set<string>) => void
}

/**
 * Display Weekdays in checkbox
 */
const WallboxWeekDaysDialog: React.FC<WallboxWeekDaysDialogProps> = ({ onClose, currentWeekDayIndex, updateSelectedDays }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const { classes } = useStyles()

  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set())

  const getWeekDayLabel = (weekDay: Day): string => t(`systemSettings.wallbox.settings.day${dayToIndex[weekDay]}`)
  const selectedTranslation = t('systemSettings.wallbox.charging-strategy.week-days.dialog.selected')
  const getSelectedDayLabel = (weekDay: Day): string => `${getWeekDayLabel(weekDay)} - ${selectedTranslation}`

  const handleDayToggle = (weekDay: Day): void => {
    setSelectedDays(prev => {
      /* copy previous values */
      const newSet = new Set(prev)
      if (newSet.has(weekDay)) newSet.delete(weekDay)
      else newSet.add(weekDay)

      return newSet
    })
  }

  const isSelected = (weekDay: Day): boolean => selectedDays.has(weekDay)

  const handleSave = (): void => {
    updateSelectedDays(selectedDays)
    onClose?.()
  }

  const modalActions = {
    primary: {
      name: t('systemSettings.wallbox.charging-strategy.week-days.dialog.button.transfer'),
      onClick: handleSave,
    },
    secondary: { name: t('common.cancel') },
  }

  return (
    <Dialog
      title={t('systemSettings.wallbox.charging-strategy.week-days.dialog.title')}
      onClose={onClose}
      actions={modalActions}
      maxWidth="xs"
      className={classes.dialog}
    >
      <FormGroup sx={{ mt: 4, mb: 4 }}>
        {ALL_DAYS.map(weekDay => {
          const weekDayIndex = CHARGING_DAYS.indexOf(weekDay)
          const isCurrentWeekDay = weekDayIndex === currentWeekDayIndex

          return (
            <StyledFormControlLabel
              key={weekDay}
              control={<Checkbox checked={isSelected(weekDay)} onChange={() => handleDayToggle(weekDay)} />}
              label={
                <Typography
                  className={classes.checkboxText}
                  variant="body2"
                  color={isCurrentWeekDay ? theme.palette.grey.grey03 : theme.palette.text.primary}
                >
                  {isCurrentWeekDay ? getSelectedDayLabel(weekDay) : getWeekDayLabel(weekDay)}
                </Typography>
              }
              disabled={isCurrentWeekDay}
              checked={isSelected(weekDay)}
            />
          )
        })}
      </FormGroup>
    </Dialog>
  )
}

export default WallboxWeekDaysDialog
